/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardCheck,
  CheckCircle2,
  Car,
  Phone,
  ChevronDown,
  ShieldCheck,
  Users,
  Banknote,
  HandCoins,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import api from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";

const CREDIT_RANGES = [
  "Excellent (740+)",
  "Good (700–739)",
  "Fair (640–699)",
  "Building Credit (<640)",
  "First-Time Buyer",
];
const EMPLOYMENT_STATUS = [
  "Employed full-time",
  "Employed part-time",
  "Self-employed",
  "Retired",
  "Active military / Veteran",
  "Other",
];
const HOUSING_STATUS = ["Own", "Rent", "Live with family", "Other"];

const STEPS = [
  {
    Icon: ClipboardCheck,
    title: "1. Apply Online",
    body: "Fill out our quick application below — it takes about 3 minutes. A soft credit pull won't affect your score.",
  },
  {
    Icon: Phone,
    title: "2. We Call You",
    body: "Kendall personally calls every applicant within one business day to talk through your options.",
  },
  {
    Icon: Car,
    title: "3. Drive Home",
    body: "Pick your vehicle, sign at the lot, and drive home the same day in most cases.",
  },
];

const BENEFITS = [
  { Icon: HandCoins, title: "Every credit story welcome", body: "Excellent, fair, building, or rebuilding — we work with families at every stage." },
  { Icon: ShieldCheck, title: "Honest pre-approvals", body: "We tell you up front what you qualify for. No surprises at the signing table." },
  { Icon: Banknote, title: "Competitive rates", body: "We shop multiple lenders so you get the best rate available for your situation." },
  { Icon: Users, title: "Treated like family", body: "You'll talk to the owner directly. No back-and-forth with finance managers you've never met." },
];

const FAQ = [
  {
    q: "Will applying hurt my credit score?",
    a: "No. Our initial application is a soft inquiry that does not affect your credit score. A hard pull only happens once you choose a vehicle and we run a formal application with a lender.",
  },
  {
    q: "What documents will I need?",
    a: "Typically: a valid driver's license, proof of income (recent pay stubs or bank statements for self-employed), and proof of residence. We'll walk you through the exact list after we talk.",
  },
  {
    q: "I have bad credit. Can you still help?",
    a: "Absolutely. We work with lenders that specialize in helping families rebuild their credit through an auto loan. Many of our customers come to us specifically because we don't turn anyone away without trying.",
  },
  {
    q: "How much do I need for a down payment?",
    a: "It depends on the vehicle and the lender — but we'll work with you to find a payment structure that fits your budget. Some customers drive off with zero down; others choose to put more down to lower their monthly payment.",
  },
  {
    q: "Can I get pre-approved before I pick a car?",
    a: "Yes — and we recommend it. Pre-approval makes the buying process faster and tells you exactly what you can afford so you can shop confidently.",
  },
  {
    q: "Do you offer in-house financing?",
    a: "We work with a network of lenders, and we'll let you know what makes the most sense for your situation. Give us a call at " + BRAND.phone + " and we'll walk through your options.",
  },
];

import { useSeo } from "@/lib/seo";

export default function Financing() {
  useSeo(
    "Auto Financing | Pit Stop Motors — Nampa, Idaho",
    "Easy, honest auto financing at Pit Stop Motors in Nampa, Idaho. Apply online in minutes — all credit situations welcome."
  );
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    desired_amount: "",
    annual_income: "",
    credit_range: "",
    employment: "",
    housing: "",
    desired_vehicle: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const noteParts = [];
      if (form.employment) noteParts.push(`Employment: ${form.employment}`);
      if (form.housing) noteParts.push(`Housing: ${form.housing}`);
      if (form.desired_vehicle) noteParts.push(`Interested vehicle: ${form.desired_vehicle}`);
      if (form.message) noteParts.push(`Notes: ${form.message}`);

      await api.post("/inquiries", {
        type: "financing",
        name: form.name,
        email: form.email,
        phone: form.phone,
        desired_amount: form.desired_amount ? parseFloat(form.desired_amount) : null,
        annual_income: form.annual_income ? parseFloat(form.annual_income) : null,
        credit_range: form.credit_range || null,
        message: noteParts.join("\n"),
      });
      toast.success("Application received! Kendall will reach out within one business day.");
      setForm({
        name: "", email: "", phone: "", desired_amount: "", annual_income: "",
        credit_range: "", employment: "", housing: "", desired_vehicle: "", message: "",
      });
    } catch (err) {
      toast.error("Couldn't submit. Please call us at " + BRAND.phone + ".");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="financing-page">
      {/* HERO */}
      <section className="bg-primary text-primary-foreground" data-testid="financing-hero">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="overline !text-accent-bright">Financing</p>
            <h1 className="mt-3 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05]">
              Drive today.<br />
              <span className="italic text-accent-bright">Built around your budget.</span>
            </h1>
            <p className="mt-6 max-w-lg text-primary-foreground/75 leading-relaxed">
              Whether you've got perfect credit or you're rebuilding, we help Idaho families get into the right vehicle with payments that actually work for their life.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm h-12 px-8" data-testid="hero-apply-cta">
                <a href="#apply">Apply Now</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-sm h-12 px-8 border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary" data-testid="hero-call-cta">
                <a href={BRAND.phoneHref}><Phone className="h-4 w-4 mr-2" /> {BRAND.phone}</a>
              </Button>
            </div>
          </div>

          <div className="hidden lg:block bg-background/95 backdrop-blur-sm border border-soft p-8" data-testid="hero-stat-card">
            <p className="overline">Why families choose us</p>
            <ul className="mt-5 space-y-4">
              {BENEFITS.slice(0, 3).map((b) => (
                <li key={b.title} className="flex gap-3">
                  <b.Icon className="h-5 w-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-primary font-medium text-sm">{b.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{b.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="border-y border-soft bg-card" data-testid="financing-steps">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-16">
          <p className="overline">How It Works</p>
          <h2 className="mt-3 font-serif text-4xl text-primary tracking-tight">Three steps to driving home.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={i} className="border border-soft bg-background p-8" data-testid={`step-${i}`}>
                <s.Icon className="h-8 w-8 text-accent" strokeWidth={1.3} />
                <h3 className="mt-4 font-serif text-2xl text-primary">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20" data-testid="financing-benefits">
        <p className="overline">Built for Real Life</p>
        <h2 className="mt-3 font-serif text-4xl text-primary tracking-tight">What you get with Pit Stop financing.</h2>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENEFITS.map((b, i) => (
            <div key={i} className="border border-soft bg-card p-6" data-testid={`benefit-${i}`}>
              <b.Icon className="h-7 w-7 text-accent" strokeWidth={1.3} />
              <h3 className="mt-4 font-serif text-xl text-primary">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="bg-primary text-primary-foreground" data-testid="financing-application">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="overline !text-accent-bright">Application</p>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight leading-tight">
              Apply in about<br /> three minutes.
            </h2>
            <p className="mt-5 text-primary-foreground/70 leading-relaxed">
              Submitting this form starts a conversation &mdash; not a hard credit pull. Kendall will personally call you within one business day to talk through what makes sense.
            </p>
            <div className="mt-8 space-y-3 text-sm text-primary-foreground/70">
              <p className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Soft pull only — no impact on your score</p>
              <p className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Pre-approval typically same day</p>
              <p className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Honest answers, real numbers</p>
            </div>
          </div>

          <div className="lg:col-span-8 bg-background text-foreground border border-soft p-8 md:p-10" data-testid="financing-form-wrap">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="financing-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="f-name">Full Name *</Label>
                  <Input id="f-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="financing-page-name" />
                </div>
                <div>
                  <Label htmlFor="f-phone">Phone *</Label>
                  <Input id="f-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="financing-page-phone" />
                </div>
              </div>
              <div>
                <Label htmlFor="f-email">Email *</Label>
                <Input id="f-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="financing-page-email" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="f-amount">Loan Amount ($)</Label>
                  <Input id="f-amount" type="number" placeholder="e.g. 18000" value={form.desired_amount} onChange={(e) => setForm({ ...form, desired_amount: e.target.value })} data-testid="financing-page-amount" />
                </div>
                <div>
                  <Label htmlFor="f-income">Annual Income ($)</Label>
                  <Input id="f-income" type="number" placeholder="e.g. 55000" value={form.annual_income} onChange={(e) => setForm({ ...form, annual_income: e.target.value })} data-testid="financing-page-income" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Credit Range</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-between mt-1 font-normal" data-testid="financing-page-credit-trigger">
                        <span className={form.credit_range ? "" : "text-muted-foreground"}>
                          {form.credit_range || "Select"}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
                      {CREDIT_RANGES.map((c) => (
                        <DropdownMenuItem key={c} onSelect={() => setForm({ ...form, credit_range: c })}>{c}</DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div>
                  <Label>Employment</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-between mt-1 font-normal" data-testid="financing-page-employment-trigger">
                        <span className={form.employment ? "" : "text-muted-foreground"}>
                          {form.employment || "Select"}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
                      {EMPLOYMENT_STATUS.map((c) => (
                        <DropdownMenuItem key={c} onSelect={() => setForm({ ...form, employment: c })}>{c}</DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Housing</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-between mt-1 font-normal" data-testid="financing-page-housing-trigger">
                        <span className={form.housing ? "" : "text-muted-foreground"}>
                          {form.housing || "Select"}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
                      {HOUSING_STATUS.map((c) => (
                        <DropdownMenuItem key={c} onSelect={() => setForm({ ...form, housing: c })}>{c}</DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div>
                  <Label htmlFor="f-vehicle">Vehicle of Interest (optional)</Label>
                  <Input id="f-vehicle" placeholder="e.g. 2022 Toyota Highlander" value={form.desired_vehicle} onChange={(e) => setForm({ ...form, desired_vehicle: e.target.value })} data-testid="financing-page-vehicle" />
                </div>
              </div>

              <div>
                <Label htmlFor="f-message">Anything else we should know?</Label>
                <Textarea id="f-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="financing-page-message" />
              </div>

              <div className="pt-2">
                <Button type="submit" disabled={submitting} size="lg" className="rounded-sm bg-accent text-accent-foreground hover:bg-accent/90 w-full md:w-auto px-10" data-testid="financing-page-submit">
                  {submitting ? "Submitting…" : "Submit Application"}
                </Button>
                <p className="mt-3 text-xs text-muted-foreground">
                  By submitting, you agree we may contact you about your application. Your information is never sold or shared with third parties outside of the lenders we shop with on your behalf.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-6 md:px-10 py-20" data-testid="financing-faq">
        <p className="overline">Common Questions</p>
        <h2 className="mt-3 font-serif text-4xl text-primary tracking-tight">Frequently asked.</h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
          {FAQ.map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-soft">
              <AccordionTrigger className="font-serif text-lg text-primary text-left hover:text-accent" data-testid={`faq-trigger-${i}`}>
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="bg-card border-t border-soft" data-testid="financing-cta">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-primary">Questions? Just call.</h2>
          <p className="mt-3 text-muted-foreground">
            Kendall picks up the phone. No call centers, no scripts.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="rounded-sm h-12 px-8" data-testid="cta-call">
              <a href={BRAND.phoneHref}><Phone className="h-4 w-4 mr-2" /> {BRAND.phone}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-sm h-12 px-8" data-testid="cta-browse">
              <Link to="/inventory">Browse Inventory</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
